import { useEffect, useState, useRef } from "react";
import { useAdmin } from "@/hooks/useAdmin";

type DiagEvent = {
  t: number;
  type: "F" | "B" | "V" | "L";
  detail: string;
};

/**
 * KeyboardDiagModalV2 — non-blocking diagnostic strip for chat flicker.
 *
 * Investigates the FLICKER-AFTER-FOCUS bug discovered June 23, 2026:
 *   "i tap into the text box to respond and thats when it starts flickering"
 *
 * Reproduces in both Safari and Median-wrapped iOS, so this is NOT a Median
 * issue. Most likely candidates the strip will surface:
 *   - visualViewport wobble during keyboard animation (multiple V events)
 *   - Layout shifts that keep firing while keyboard is open (multiple L events)
 *   - Focus/blur oscillation (interleaved F/B events)
 *
 * Renders only for admin. Pointer-events: none so it can't block taps.
 *
 * TO ENABLE:
 *   - Add ?diag=1 to URL (auto-persists to localStorage)
 *   - Or in DevTools console: localStorage.setItem('kin_diag_v2', 'on')
 *
 * TO DISABLE:
 *   - Add ?diag=0 to URL
 *   - Or in DevTools console: localStorage.removeItem('kin_diag_v2')
 *
 * Event types:
 *   F = focusin on textarea/input (typing started)
 *   B = focusout on textarea/input (focus lost)
 *   V = visualViewport resize with height in px (keyboard moved)
 *   L = layout-shift entry with shift score (something visually moved)
 *
 * Remove this entire file + its import in Index.tsx once the flicker is fixed.
 */
export default function KeyboardDiagModalV2() {
  const isAdmin = useAdmin();
  const [enabled, setEnabled] = useState(false);
  const [events, setEvents] = useState<DiagEvent[]>([]);
  const startRef = useRef(performance.now());

  // Honor ?diag=1 / ?diag=0 in URL (auto-persists to localStorage so the
  // toggle survives page reload / SW activation). Same pattern as the
  // existing ?tab= deep-link in Index.tsx.
  useEffect(() => {
    if (!isAdmin) return;
    try {
      const params = new URLSearchParams(window.location.search);
      const diagParam = params.get("diag");
      if (diagParam === "1") {
        localStorage.setItem("kin_diag_v2", "on");
        params.delete("diag");
        const next = params.toString();
        const url =
          window.location.pathname +
          (next ? `?${next}` : "") +
          window.location.hash;
        window.history.replaceState({}, "", url);
      } else if (diagParam === "0") {
        localStorage.removeItem("kin_diag_v2");
        params.delete("diag");
        const next = params.toString();
        const url =
          window.location.pathname +
          (next ? `?${next}` : "") +
          window.location.hash;
        window.history.replaceState({}, "", url);
      }
      setEnabled(localStorage.getItem("kin_diag_v2") === "on");
    } catch {
      // localStorage / URLSearchParams not available — bail silently
    }
  }, [isAdmin]);

  useEffect(() => {
    if (!enabled) return;

    const addEvent = (type: DiagEvent["type"], detail: string) => {
      setEvents((prev) => {
        const next = [
          ...prev,
          {
            t: Math.round(performance.now() - startRef.current),
            type,
            detail,
          },
        ];
        // Keep last 40 entries (enough for several seconds of dense activity)
        return next.slice(-40);
      });
    };

    // --- Focus / blur on text inputs (bubbling versions of focus/blur) ---
    const onFocusIn = (e: FocusEvent) => {
      const t = e.target as HTMLElement | null;
      if (t?.tagName === "TEXTAREA" || t?.tagName === "INPUT") {
        addEvent("F", t.tagName.toLowerCase());
      }
    };
    const onFocusOut = (e: FocusEvent) => {
      const t = e.target as HTMLElement | null;
      if (t?.tagName === "TEXTAREA" || t?.tagName === "INPUT") {
        addEvent("B", t.tagName.toLowerCase());
      }
    };
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("focusout", onFocusOut);

    // --- visualViewport changes (keyboard movement) ---
    const vv = window.visualViewport;
    let lastVVH = vv?.height ?? 0;
    const onVV = () => {
      const h = Math.round(vv?.height ?? 0);
      // Ignore single-pixel jitter — only log meaningful changes
      if (Math.abs(h - lastVVH) >= 2) {
        lastVVH = h;
        addEvent("V", `h=${h}`);
      }
    };
    vv?.addEventListener("resize", onVV);

    // --- Layout shifts (the smoking gun for visible movement) ---
    let observer: PerformanceObserver | undefined;
    try {
      observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          const ls = entry as PerformanceEntry & { value?: number };
          const score = ls.value ?? 0;
          if (score > 0.001) {
            addEvent("L", score.toFixed(3));
          }
        }
      });
      // `layout-shift` is a Chrome/Safari standard performance entry.
      // Some older Safari versions may not support it — wrap in try/catch.
      observer.observe({ type: "layout-shift", buffered: false } as PerformanceObserverInit);
    } catch {
      // Layout-shift not supported in this browser — diagnostic still works
      // for F/B/V events, just without the L data point.
    }

    return () => {
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("focusout", onFocusOut);
      vv?.removeEventListener("resize", onVV);
      observer?.disconnect();
    };
  }, [enabled]);

  if (!isAdmin || !enabled) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: "calc(env(safe-area-inset-top, 0px) + 4px)",
        left: 8,
        right: 8,
        zIndex: 99999,
        pointerEvents: "none",
        background: "rgba(28, 26, 62, 0.92)",
        color: "#9090E8",
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        fontSize: 10,
        padding: 6,
        borderRadius: 6,
        maxHeight: "45vh",
        overflow: "hidden",
        lineHeight: 1.35,
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
      }}
    >
      <div
        style={{
          color: "#F0F0FF",
          fontWeight: 600,
          marginBottom: 4,
          fontSize: 9,
          letterSpacing: 0.3,
        }}
      >
        DIAG v2 · F=focus B=blur V=viewport L=layoutshift
      </div>
      {events
        .slice()
        .reverse()
        .map((e, i) => (
          <div
            key={`${e.t}-${i}`}
            style={{
              whiteSpace: "nowrap",
              color:
                e.type === "L"
                  ? "#FFB4B4" // red-ish — layout shifts are the suspect
                  : e.type === "V"
                  ? "#FFE0A0" // yellow — viewport changes are the cause
                  : "#9090E8", // lavender — focus events are baseline
            }}
          >
            {e.t.toString().padStart(6, " ")}ms · {e.type} · {e.detail}
          </div>
        ))}
    </div>
  );
}
